import { BookingSegmentFills } from 'containers/Booking/components/BookingSegmentFills/data/BookingSegmentFills.query';
import {
  invalidateFocusGroupSessions,
  reconcileFocusGroupSessionChanges,
} from 'containers/FocusGroups/data/focusGroupSessionsCache';
import { addTagToCache, removeTagFromCache } from 'containers/Tags/data/tagsCache';
import {
  addHighlightToCache,
  removeHighlightFromCache,
  removeNoteFromCache,
} from 'containers/Transcript/annotations/data/annotationsCache';

import type { BookingSegmentFillsUpdated } from 'containers/Booking/components/BookingSegmentFills/data/BookingSegmentFillsUpdated.subscription';
import type { FocusGroupSessionsUpdated } from 'containers/FocusGroups/data/FocusGroupSchedule.subscription';
import type { TagEvents } from 'containers/Tags/data/TagEvents.subscription';
import type { HighlightEvents } from 'containers/Transcript/annotations/data/HighlightEvents.subscription';
import type { RoomNoteEvents } from 'containers/Transcript/annotations/data/RoomNoteEvents.subscription';

import type { Cache } from '@urql/exchange-graphcache';
import type { ResultOf, VariablesOf } from 'gql.tada';

export const subscriptionUpdates = {
  // The subscription and query share a parent shape but different root field
  // names. graphcache normalizes the segment entries by `_id`, but `unassigned`
  // and `total_participants` are embedded in the parent and would otherwise stay
  // stale in the query cache until a refetch.
  bookingSegmentFillsUpdated: (
    result: ResultOf<typeof BookingSegmentFillsUpdated>,
    args: VariablesOf<typeof BookingSegmentFillsUpdated>,
    cache: Cache,
  ) => {
    const updated = result.bookingSegmentFillsUpdated;
    if (!updated) return;

    cache.inspectFields('Query').forEach(field => {
      if (field.fieldName !== 'bookingSegmentFills') return;
      // Cache args were written by us when issuing this query, so they match the query's variable schema.
      const fieldArgs = field.arguments as VariablesOf<typeof BookingSegmentFills> | null;
      if (fieldArgs?.input?._booking_id !== args._booking_id) return;

      const requestedIds = fieldArgs.input._segment_ids;
      const segments = requestedIds?.length
        ? updated.segments.filter(segment => requestedIds.includes(segment._id))
        : updated.segments;

      cache.updateQuery({ query: BookingSegmentFills, variables: fieldArgs }, () => ({
        bookingSegmentFills: { ...updated, segments },
      }));
    });
  },

  // `full: false` carries the sessions that changed, which graphcache merges into the cached weeks
  // by id; this only re-reads a week whose set of sessions the push changed. `full: true` carries
  // nothing, so every cached week and the summary are marked stale and the one on screen re-reads.
  focusGroupSessionsUpdated: (
    result: ResultOf<typeof FocusGroupSessionsUpdated>,
    args: VariablesOf<typeof FocusGroupSessionsUpdated>,
    cache: Cache,
  ) => {
    const update = result.focusGroupSessionsUpdated;

    if (update.full) {
      invalidateFocusGroupSessions({ cache, bookingId: args.booking_id, withSummary: true });
      return;
    }
    reconcileFocusGroupSessionChanges({ cache, bookingId: args.booking_id, sessions: update.sessions });
  },

  // An update merges into the cache by `_id`. A create or delete changes which items a list holds.
  highlight: (result: ResultOf<typeof HighlightEvents>, args: { meeting_code: string }, cache: Cache) => {
    const highlight = result.highlight?.highlight;
    const event = result.highlight?.meta?.event;
    if (highlight && event === 'highlight_create') {
      addHighlightToCache(cache, args.meeting_code, highlight);
    } else if (highlight && event === 'highlight_delete') {
      removeHighlightFromCache(cache, highlight._id);
    }
  },

  // A created note names no room, so `useRoomNotes` refetches for one it does not hold.
  videoRoomNote: (result: ResultOf<typeof RoomNoteEvents>, args: { filter: { code: string } }, cache: Cache) => {
    const note = result.videoRoomNote?.note;
    if (note && result.videoRoomNote?.type === 'delete') {
      removeNoteFromCache(cache, args.filter.code, note._id);
    }
  },

  tags: (result: ResultOf<typeof TagEvents>, args: { input: { _team_id?: string | null } }, cache: Cache) => {
    const teamId = args.input._team_id;
    const event = result.tags?.meta?.event;
    (result.tags?.tags ?? []).forEach(tag => {
      if (!tag || !teamId) {
        return;
      }
      if (event === 'tags_create') {
        addTagToCache(cache, teamId, tag);
      } else if (event === 'tags_delete') {
        removeTagFromCache(cache, teamId, tag._id);
      }
    });
  },
};
